import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable.tsx';
import { ShopData, TrolleyDataItem } from './types.ts';
import { ShopId } from '@core/store/slices/apiSlice.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { dashboardService } from '@/services';

export interface ShopsProps {
    shopIds: ShopId[];
    selectable?: boolean;
    value?: ShopData[];
    onChange?: (selected: ShopData[]) => void;
    onLoading?: () => void;
    onLoaded?: () => void;
}

export interface ShopsRef {
    load: () => Promise<void>;
}

const Shops = forwardRef<ShopsRef, ShopsProps>(
    ({ shopIds, selectable = false, value = [], onChange, onLoading, onLoaded }, ref) => {
        const { t } = useTranslation();
        const { notifyErrorResponse } = useNotifications();
        const [loading, setLoading] = useState(false);
        const [trolleyData, setTrolleyData] = useState<ShopData[]>([]);

        // Extract unique group IDs from shop data
        const groupIds = useMemo(() => {
            const ids = shopIds.flatMap((shop) => shop.fastLanerGroupIds || []).filter(Boolean);
            return [...new Set(ids)];
        }, [shopIds]);

        const getTrolleyData = useCallback(async (groupIds: string[]) => {
            if (groupIds.length === 0) {
                return [];
            }

            try {
                const response = await dashboardService.getFastLanerTrolleyData(groupIds);
                return (response as any)?.data || response || [];
            } catch (error) {
                notifyErrorResponse(error);
                return [];
            }
        }, []);

        const load = useCallback(async () => {
            setLoading(true);
            onLoading?.();

            try {
                const data = await getTrolleyData(groupIds);

                const processedData = shopIds
                    .filter((shop) => {
                        if (selectable) {
                            return shop.fastLanerGroupIds?.length && shop.fastLanerGroupIds.length > 0;
                        }
                        return true;
                    })
                    .map((shop) => {
                        const matchingData = data.filter((trolleyInfo: TrolleyDataItem) =>
                            shop.fastLanerGroupIds?.includes(trolleyInfo.groupId)
                        );

                        const apks = [
                            ...new Set(matchingData.flatMap((item: TrolleyDataItem) => item.expectedAppVersions || []))
                        ].map((apk) => {
                            const [name, version] = (apk as string).split(':');
                            return { name, version };
                        });

                        return {
                            name: shop.name,
                            fastLanerGroupIds: shop.fastLanerGroupIds || [],
                            apks
                        };
                    });

                setTrolleyData(processedData);
            } finally {
                setLoading(false);
                onLoaded?.();
            }
        }, [groupIds, shopIds, selectable]);

        useImperativeHandle(ref, () => ({
            load
        }));

        useEffect(() => {
            const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, load);
            load();
            return () => {
                unsubscribeReload();
            };
        }, [load]);

        // Define columns
        const columns: DataTableColumn<ShopData>[] = useMemo(
            () => [
                {
                    text: t('shop'),
                    value: 'name',
                    width: 200,
                    render: (row) => row.name
                },
                {
                    text: t('activeApks'),
                    value: 'activeApks',
                    width: 200,
                    render: (row) => `${row.apks.length} ${t('activeApks')}`
                }
            ],
            [t]
        );

        // Render expanded content for nested APKs table
        const renderExpandedContent = useCallback(
            (shop: ShopData) => (
                <Box sx={{ pl: 8 }}>
                    <Table
                        size="small"
                        sx={{
                            '& .MuiTableCell-root': {
                                py: 1
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('name')}</TableCell>
                                <TableCell>{t('version')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shop.apks.map((apk, index) => (
                                <TableRow key={`${apk.name}-${apk.version}-${index}`}>
                                    <TableCell>{apk.name}</TableCell>
                                    <TableCell>{apk.version}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ),
            [t]
        );

        const expansionConfig = selectable
            ? undefined
            : {
                  getRowKey: (item: ShopData) => item.name,
                  renderExpandedContent,
                  showExpandIconCondition: (row: ShopData) => row.apks.length > 0
              };

        return (
            <NewWcnTable<ShopData>
                title={selectable ? t('fastLanerShopSelection') : undefined}
                columns={columns}
                data={trolleyData}
                loading={loading}
                pagination={{
                    count: trolleyData.length,
                    rowsPerPage: 10,
                    rowsPerPageOptions: [10, 15, 30]
                }}
                selection={
                    selectable
                        ? {
                              selectedItems: value,
                              onSelectionChange: onChange || (() => {}),
                              getItemKey: (item) => item.name,
                              selectionMode: 'multiple'
                          }
                        : undefined
                }
                expansion={expansionConfig}
                noDataMessage={t('noData')}
                size="small"
            />
        );
    }
);

Shops.displayName = 'Shops';

export default Shops;
